import {
  convertHypothesisItems,
  getCartItemProperties,
  getValidCartItems,
} from '../../utils/cart';
import {
  messagingActions,
  TextFrameTransmissionMessage,
} from '../../reducers/messagingSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  selectCartItems,
  selectCartItemsQuantity,
  selectHypothesisCartItems,
} from '../../selectors/cart';
import {
  selectAutoComboCartItems,
  selectAutoComboCartItemsQuantity,
} from '../../redux/features/autoCombo/autoCombo.selector';
import { useSelector } from 'react-redux';
import { selectActiveRestaurantDetails } from '../../selectors/restaurant';
import { IBrandMenuOrderItem } from '../../types/brandMenu';
import { buildUnifiedMenuModuleOrder } from '../../utils/menuUnification';
import useAutoCombo from '../autoCombo/useAutoCombo.hooks';
import { selectReadbackTemplateVersion } from '../../redux/features/config/config.selector';
import { selectBrandMenuVersion } from '../../selectors/menu';
import logger from '../../utils/logger';

const useSendMessageHooks = () => {
  const dispatch = useAppDispatch();
  const { restaurantCode } = useSelector(selectActiveRestaurantDetails);
  const cartItemsQuantity = useAppSelector(selectCartItemsQuantity);
  const cartItems = useAppSelector(selectCartItems);
  const hypothesisCartItems = useAppSelector(selectHypothesisCartItems);
  const readbackTemplateVersion = useAppSelector(selectReadbackTemplateVersion);
  const brandMenuVersion = useAppSelector(selectBrandMenuVersion);

  const autoComboCartItemsQuantity = useAppSelector(
    selectAutoComboCartItemsQuantity
  );
  const autoComboCartItems = useAppSelector(selectAutoComboCartItems);
  const { isUnifiedMenu, isPRPAutoComboEnabled, areItemsAutoCombined } =
    useAutoCombo();

  const itemsQuantity = areItemsAutoCombined
    ? autoComboCartItemsQuantity
    : cartItemsQuantity;
  const validCartItems = areItemsAutoCombined
    ? getValidCartItems(autoComboCartItems)
    : getValidCartItems(cartItems);

  const sendMessage = (message: string, metadata?: any) => {
    const payload: Partial<TextFrameTransmissionMessage> = {
      data: { payload: message, status: 'final', metadata },
    };
    dispatch(messagingActions.sendMessage(payload as any));
  };

  const sendReadBackMessage = (intent: string, cTimer: number) => {
    // Send the order items as metadata for dt-bridge to always have the latest cart state even if it's not completed or canceled
    let orderItems = [];
    if (isUnifiedMenu) {
      const menuModuleItems: IBrandMenuOrderItem[] = [];
      Object.values(validCartItems).forEach((item) => {
        const menuModuleResult = buildUnifiedMenuModuleOrder(
          item,
          itemsQuantity,
          false,
          true
        );
        if (menuModuleResult) {
          menuModuleItems.push(menuModuleResult);
        }
      });
      orderItems = menuModuleItems;
    } else {
      const autoComboItemPopulated =
        isPRPAutoComboEnabled && Object.values(autoComboCartItems).length;
      const cartItemToUse = autoComboItemPopulated
        ? autoComboCartItems
        : cartItems;
      const cartItemsQuantityToUse = autoComboItemPopulated
        ? autoComboCartItemsQuantity
        : cartItemsQuantity;
      orderItems = Object.values(cartItemToUse).map((item) => {
        let groupId: string[] = [];

        const cartItem = getCartItemProperties(
          item,
          groupId,
          [],
          restaurantCode || '',
          cartItemsQuantityToUse
        );
        return cartItem;
      });
    }

    if (Object.values(hypothesisCartItems).length) {
      orderItems = [
        ...orderItems,
        ...convertHypothesisItems(Object.values(hypothesisCartItems)),
      ];
    }

    const includeUseTemplate =
      isUnifiedMenu &&
      readbackTemplateVersion > 0 &&
      Number(brandMenuVersion || 0) >= readbackTemplateVersion;

    logger.log({
      message: `Should include useTemplate in readback: ${includeUseTemplate}`,
      readbackTemplateVersion,
      brandMenuVersion,
    });
    sendMessage('', {
      intent,
      timeDelta: cTimer,
      orderItems,
      ...(isUnifiedMenu
        ? {
            brandMenuOrder: true,
            ...(includeUseTemplate ? { useTemplate: true } : null),
          }
        : null),
    });
  };

  return {
    sendMessage,
    sendReadBackMessage,
  };
};
export default useSendMessageHooks;
